import React from 'react';
import {
  Box,
  Flex,
  Container,
  Heading,
  Text,
  Image,
  Link,
  Button,
  SimpleGrid,
  Icon,
  HStack,
  VStack,
  keyframes,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { IoLogoLinkedin, IoLogoGithub, IoMail } from 'react-icons/io5';
import { FiCode, FiUsers, FiShoppingBag, FiTrendingUp, FiArrowRight } from 'react-icons/fi';
import Me from '../assets/me.jpg';
import Willdan from '../assets/willdan.png';
import Goldenwait from '../assets/Goldenwait.png';

const MotionFlex = motion(Flex);
const MotionBox = motion(Box);
const MotionHeading = motion(Heading);

const pulseGlow = keyframes`
  0% { transform: scale(0.98); opacity: 0.5; }
  50% { transform: scale(1.02); opacity: 0.8; }
  100% { transform: scale(0.98); opacity: 0.5; }
`;

function Home() {
  return (
    <Box bg={'#070709'} minH={'100vh'} color={'white'} overflow={'hidden'} position={'relative'}>
      {/* Dynamic Background Gradients */}
      <Box
        position={'absolute'}
        top={'-10%'}
        left={'-10%'}
        w={'60vw'}
        h={'60vw'}
        borderRadius={'full'}
        bg={'radial-gradient(circle, rgba(239, 68, 68, 0.12) 0%, rgba(0,0,0,0) 70%)'}
        filter={'blur(80px)'}
        zIndex={0}
        pointerEvents={'none'}
      />
      <Box
        position={'absolute'}
        bottom={'20%'}
        right={'-10%'}
        w={'50vw'}
        h={'50vw'}
        borderRadius={'full'}
        bg={'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, rgba(0,0,0,0) 70%)'}
        filter={'blur(80px)'}
        zIndex={0}
        pointerEvents={'none'}
      />

      <Container maxW={'6xl'} position={'relative'} zIndex={1}>
        {/* HERO SECTION */}
        <MotionFlex
          direction={{ base: 'column-reverse', md: 'row' }}
          align={'center'}
          justify={'space-between'}
          py={{ base: 16, md: 28 }}
          gap={{ base: 12, md: 8 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Hero text */}
          <VStack align={'flex-start'} spacing={6} maxW={'2xl'}>
            <HStack spacing={2}>
              <Box w={'8px'} h={'8px'} borderRadius={'full'} bg={'red.500'} />
              <Text
                fontSize={'sm'}
                fontWeight={800}
                letterSpacing={'widest'}
                textTransform={'uppercase'}
                color={'red.500'}
              >
                Available for Roles
              </Text>
            </HStack>
            <VStack align={'flex-start'} spacing={2}>
              <MotionHeading
                as={'h1'}
                fontSize={{ base: '4xl', md: '6xl' }}
                fontWeight={900}
                lineHeight={'1.1'}
              >
                Hi, I'm{' '}
                <Text as={'span'} bgGradient={'linear(to-r, red.500, red.400)'} bgClip={'text'}>
                  Ted Nguyen
                </Text>
              </MotionHeading>
              <Text
                fontSize={{ base: 'xl', md: '2xl' }}
                fontWeight={700}
                color={'whiteAlpha.800'}
              >
                Software Engineer & Technical Leader
              </Text>
            </VStack>
            <Text fontSize={'lg'} color={'whiteAlpha.700'} lineHeight={'tall'}>
              I'm an experienced software leader specializing in full-stack engineering, 
              high-scale e-commerce, and conversion optimization. With a background blending 
              deep technology development and growth marketing, I build performant digital products 
              and guide high-performing developer teams.
            </Text>
            
            <Flex direction={{ base: 'column', sm: 'row' }} gap={4} w={'full'} pt={2}>
              <Button
                as={Link}
                href="/work"
                bgGradient={'linear(to-r, red.500, red.600)'}
                color={'white'}
                _hover={{
                  bgGradient: 'linear(to-r, red.600, red.700)',
                  boxShadow: '0 0 20px rgba(239, 68, 68, 0.4)',
                  textDecoration: 'none',
                }}
                size={'lg'}
                rightIcon={<FiArrowRight />}
                px={8}
                borderRadius={'full'}
                fontWeight={700}
              >
                Explore My Work
              </Button>
              <Button
                as={Link}
                href="https://www.linkedin.com/in/ted-d-nguyen/"
                isExternal
                variant={'outline'}
                borderColor={'whiteAlpha.300'}
                color={'white'}
                _hover={{
                  bg: 'whiteAlpha.100',
                  borderColor: 'whiteAlpha.600',
                  textDecoration: 'none',
                }}
                size={'lg'}
                leftIcon={<Icon as={IoLogoLinkedin} />}
                px={8}
                borderRadius={'full'}
                fontWeight={700}
              >
                Connect on LinkedIn
              </Button>
            </Flex>

            <HStack spacing={6} pt={4} color={'whiteAlpha.600'}>
              <Link href="https://github.com/tedfish" isExternal _hover={{ color: 'white' }}>
                <Icon as={IoLogoGithub} boxSize={6} />
              </Link>
              <Link href="mailto:ted.d.nguyen@gmail.com" _hover={{ color: 'white' }}>
                <Icon as={IoMail} boxSize={6} />
              </Link>
            </HStack>
          </VStack>

          {/* Hero Image */}
          <MotionBox
            position={'relative'}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Box
              position={'absolute'}
              inset={'-6px'}
              bgGradient={'linear(to-tr, red.500, blue.500)'}
              borderRadius={'full'}
              filter={'blur(12px)'}
              animation={`${pulseGlow} 6s infinite ease-in-out`}
            />
            <Image
              src={Me}
              alt="Ted Nguyen"
              w={{ base: '260px', md: '340px' }}
              h={{ base: '260px', md: '340px' }}
              borderRadius={'full'}
              objectFit={'cover'}
              objectPosition={'top'}
              border={'6px solid'}
              borderColor={'#070709'}
              position={'relative'}
              zIndex={1}
              boxShadow={'2xl'}
            />
          </MotionBox>
        </MotionFlex>

        {/* CORE EXPERTISE SECTION */}
        <Box py={16} borderTop={'1px solid'} borderColor={'whiteAlpha.100'}>
          <VStack spacing={4} align={'center'} textAlign={'center'} mb={14}>
            <Heading as={'h2'} fontSize={'3xl'} fontWeight={800}>
              Core Expertise
            </Heading>
            <Text fontSize={'md'} color={'whiteAlpha.600'} maxW={'xl'}>
              A versatile skill set bridges technical execution, business strategy, and team development.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
            <Box
              bg={'whiteAlpha.50'}
              backdropFilter={'blur(8px)'}
              border={'1px solid'}
              borderColor={'whiteAlpha.100'}
              borderRadius={'2xl'}
              p={8}
              transition={'all 0.3s'}
              _hover={{
                transform: 'translateY(-6px)',
                borderColor: 'red.500',
                boxShadow: '0 12px 30px rgba(239, 68, 68, 0.1)',
                bg: 'whiteAlpha.100',
              }}
            >
              <Icon as={FiUsers} boxSize={8} color={'red.400'} mb={6} />
              <Heading as={'h3'} fontSize={'xl'} fontWeight={700} mb={3}>
                Tech Leadership
              </Heading>
              <Text fontSize={'sm'} color={'whiteAlpha.700'} lineHeight={'relaxed'}>
                Guiding engineering processes, building cohesive teams, and aligning technical milestones with product milestones.
              </Text>
            </Box>

            <Box
              bg={'whiteAlpha.50'}
              backdropFilter={'blur(8px)'}
              border={'1px solid'}
              borderColor={'whiteAlpha.100'}
              borderRadius={'2xl'}
              p={8}
              transition={'all 0.3s'}
              _hover={{
                transform: 'translateY(-6px)',
                borderColor: 'red.500',
                boxShadow: '0 12px 30px rgba(239, 68, 68, 0.1)',
                bg: 'whiteAlpha.100',
              }}
            >
              <Icon as={FiCode} boxSize={8} color={'red.400'} mb={6} />
              <Heading as={'h3'} fontSize={'xl'} fontWeight={700} mb={3}>
                Full-Stack Dev
              </Heading>
              <Text fontSize={'sm'} color={'whiteAlpha.700'} lineHeight={'relaxed'}>
                Architecting clean APIs, responsive frontends (React/Chakra), and managing performant backends and database infrastructures.
              </Text>
            </Box>

            <Box
              bg={'whiteAlpha.50'}
              backdropFilter={'blur(8px)'}
              border={'1px solid'}
              borderColor={'whiteAlpha.100'}
              borderRadius={'2xl'}
              p={8}
              transition={'all 0.3s'}
              _hover={{
                transform: 'translateY(-6px)',
                borderColor: 'red.500',
                boxShadow: '0 12px 30px rgba(239, 68, 68, 0.1)',
                bg: 'whiteAlpha.100',
              }}
            >
              <Icon as={FiShoppingBag} boxSize={8} color={'red.400'} mb={6} />
              <Heading as={'h3'} fontSize={'xl'} fontWeight={700} mb={3}>
                E-Commerce
              </Heading>
              <Text fontSize={'sm'} color={'whiteAlpha.700'} lineHeight={'relaxed'}>
                Developing custom Shopify Plus stores, scaling subscription models, checkout integrations, and payment gateways.
              </Text>
            </Box>

            <Box
              bg={'whiteAlpha.50'}
              backdropFilter={'blur(8px)'}
              border={'1px solid'}
              borderColor={'whiteAlpha.100'}
              borderRadius={'2xl'}
              p={8}
              transition={'all 0.3s'}
              _hover={{
                transform: 'translateY(-6px)',
                borderColor: 'red.500',
                boxShadow: '0 12px 30px rgba(239, 68, 68, 0.1)',
                bg: 'whiteAlpha.100',
              }}
            >
              <Icon as={FiTrendingUp} boxSize={8} color={'red.400'} mb={6} />
              <Heading as={'h3'} fontSize={'xl'} fontWeight={700} mb={3}>
                Growth & SEO
              </Heading>
              <Text fontSize={'sm'} color={'whiteAlpha.700'} lineHeight={'relaxed'}>
                Leveraging Google Analytics, improving SEO visibility, optimizing speed performance, and running conversion tests.
              </Text>
            </Box>
          </SimpleGrid>
        </Box>

        {/* FEATURED WORK PREVIEW */}
        <Box py={16} borderTop={'1px solid'} borderColor={'whiteAlpha.100'}>
          <Flex direction={{ base: 'column', md: 'row' }} justify={'space-between'} align={{ base: 'flex-start', md: 'flex-end' }} mb={12} gap={4}>
            <VStack align={'flex-start'} spacing={3}>
              <Heading as={'h2'} fontSize={'3xl'} fontWeight={800}>
                Featured Experience
              </Heading>
              <Text fontSize={'md'} color={'whiteAlpha.600'} maxW={'xl'}>
                A glance at some of the industry leaders and platforms I've recently collaborated with.
              </Text>
            </VStack>
            <Button
              as={Link}
              href="/work"
              variant={'ghost'}
              color={'red.400'}
              rightIcon={<FiArrowRight />}
              _hover={{
                bg: 'whiteAlpha.50',
                color: 'red.300',
                textDecoration: 'none',
              }}
              fontWeight={700}
            >
              View Full Work History
            </Button>
          </Flex>

          <SimpleGrid columns={{ base: 1, md: 2 }} gap={8}>
            {/* Willdan Card */}
            <Flex
              direction={'column'}
              bg={'whiteAlpha.50'}
              border={'1px solid'}
              borderColor={'whiteAlpha.100'}
              borderRadius={'2xl'}
              overflow={'hidden'}
              transition={'all 0.3s'}
              _hover={{
                transform: 'translateY(-4px)',
                borderColor: 'whiteAlpha.300',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
              }}
            >
              <Box height={'200px'} overflow={'hidden'} position={'relative'}>
                <Image src={Willdan} alt="Willdan Group, Inc." w={'full'} h={'full'} objectFit={'cover'} objectPosition={'top'} />
                <Box position={'absolute'} inset={0} bgGradient={'linear(to-t, #070709, transparent)'} />
              </Box>
              <VStack align={'flex-start'} p={6} spacing={3} flex={1}>
                <Heading as={'h3'} fontSize={'xl'} fontWeight={700}>
                  Willdan Group, Inc.
                </Heading>
                <Text fontSize={'sm'} fontWeight={600} color={'red.400'}>
                  Webmaster | May 2024 to Present
                </Text>
                <Text fontSize={'sm'} color={'whiteAlpha.700'} noOfLines={3}>
                  Managing the corporate web platforms, overseeing development, SEO optimizations, and platform integrity for utilities and public sector engineering solutions.
                </Text>
              </VStack>
            </Flex>

            {/* Golden Wait Card */}
            <Flex
              direction={'column'}
              bg={'whiteAlpha.50'}
              border={'1px solid'}
              borderColor={'whiteAlpha.100'}
              borderRadius={'2xl'}
              overflow={'hidden'}
              transition={'all 0.3s'}
              _hover={{
                transform: 'translateY(-4px)',
                borderColor: 'whiteAlpha.300',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
              }}
            >
              <Box height={'200px'} overflow={'hidden'} position={'relative'}>
                <Image src={Goldenwait} alt="Golden Wait" w={'full'} h={'full'} objectFit={'cover'} objectPosition={'top'} />
                <Box position={'absolute'} inset={0} bgGradient={'linear(to-t, #070709, transparent)'} />
              </Box>
              <VStack align={'flex-start'} p={6} spacing={3} flex={1}>
                <Heading as={'h3'} fontSize={'xl'} fontWeight={700}>
                  Golden Wait
                </Heading>
                <Text fontSize={'sm'} fontWeight={600} color={'red.400'}>
                  Lead Developer & Co-founder | 2022 to Present
                </Text>
                <Text fontSize={'sm'} color={'whiteAlpha.700'} noOfLines={3}>
                  Built the marketing and waitlist automation SaaS product using React and Cloudflare/Firebase architectures. Helping brands drive automated customer engagement.
                </Text>
              </VStack>
            </Flex>
          </SimpleGrid>
        </Box>

        {/* FOOTER */}
        <Flex
          direction={{ base: 'column', sm: 'row' }}
          justify={'space-between'}
          align={'center'}
          py={10}
          borderTop={'1px solid'}
          borderColor={'whiteAlpha.100'}
          color={'whiteAlpha.500'}
          fontSize={'sm'}
          gap={4}
        >
          <Text>© {new Date().getFullYear()} Ted Nguyen. All rights reserved.</Text>
          <HStack spacing={6}>
            <Link href="https://www.linkedin.com/in/ted-d-nguyen/" isExternal _hover={{ color: 'white' }}>
              LinkedIn
            </Link>
            <Link href="https://github.com/tedfish" isExternal _hover={{ color: 'white' }}>
              GitHub
            </Link>
            <Link href="mailto:ted.d.nguyen@gmail.com" _hover={{ color: 'white' }}>
              Email
            </Link>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
}

export default Home;
